import { Button, Card, Col, DatePicker, Image, Modal, Row, Select, Table, Typography, message, Tag, Avatar, Space, Spin, Dropdown, Menu, Input } from "antd";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getMurghiMandiStatsBySubType, getMurghiMandiPostsBySubType, getMurgiStatApi } from "../../../RestServices/postingService";
import { getCustomers } from "../../../RestServices/authService";
import { MAIN_BACKGROUND_COLOR } from "../../../styleConstants";
import MurghiMandiLogo from "../../../assets/Dashboard Icons/MurghiMandi.png";
import {
  FileTextOutlined,
  CheckOutlined,
  CloseOutlined,
  ShoppingCartOutlined,
  RiseOutlined,
  FallOutlined,
  EyeOutlined,
  UserOutlined,
  StarOutlined,
  CalendarOutlined,
  PieChartOutlined,
  AppstoreOutlined,
  TagsOutlined,
  TeamOutlined,
  ReloadOutlined
} from "@ant-design/icons";
import "./murghiMandiStatsBySubType.css";
import SearchIcon from "@material-ui/icons/Search";

const { Option } = Select;
const { Title } = Typography;

const MurghiMandiStatsBySubType = ({ range }) => {
  const [loading, setLoading] = useState(false);
  const [crmLoading, setCrmLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [crmStat, setCrmStat] = useState(null);
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [postsModal, setPostsModal] = useState({
    visible: false,
    posts: [],
    loading: false,
    title: "",
    pagination: {
      current: 1,
      pageSize: 20,
      total: 0,
    },
    filters: {},
  });
  const { token } = useSelector((state) => state.Authentication.user);
  const { allcities } = useSelector((state) => state.Cities);

  const [dateBetween, setDateBetween] = useState({
    startDate: moment().subtract(30, "days"),
    endDate: moment(),
  });

  // Follow the dashboard's global From/To range when provided; the local
  // pickers still allow overriding it afterwards.
  useEffect(() => {
    if (range && range[0] && range[1]) {
      setDateBetween({
        startDate: moment(range[0]),
        endDate: moment(range[1]),
      });
    }
  }, [range]);

  const kFormatter = (num) => {
    if (isNaN(num)) {
      return "0";
    }
    return Math.abs(num) > 999
      ? Math.sign(num) * (Math.abs(num) / 1000).toFixed(1) + "k"
      : Math.sign(num) * Math.abs(num);
  };

  // Format subType name for display
  const formatSubTypeName = useCallback((subType) => {
    if (!subType) return "";
    if (subType === "all") return "All Categories";
    
    let formatted = subType
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
      .replace(/^./, (str) => str.toUpperCase());
    
    const subTypeMap = {
      whiteegg: "White Egg",
      brownegg: "Brown Egg",
      whitelayer: "White Layer",
      brownlayer: "Brown Layer",
      broilerchick: "Broiler Chick",
      broiler: "Broiler",
      whitelayerchick: "White Layer Chick",
      brownlayerchick: "Brown Layer Chick",
      corn: "Corn",
      soyabean: "Soyabean",
      poultryshed: "Poultry Shed"
    };
    
    if (subTypeMap[subType.toLowerCase()]) {
      return subTypeMap[subType.toLowerCase()];
    }
    
    return formatted.trim();
  }, []);

  // Fetch statistics
  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      
      if (dateBetween.startDate) {
        params.startDate = dateBetween.startDate.format("YYYY-MM-DD");
      }
      if (dateBetween.endDate) {
        params.endDate = dateBetween.endDate.format("YYYY-MM-DD");
      }
      if (selectedCity) {
        params.city = selectedCity;
      }
      if (selectedStatus) {
        params.status = selectedStatus;
      }

      const response = await getMurghiMandiStatsBySubType(params, token);
      
      if (response.success) {
        setStats(response);
      } else {
        message.error(response.message || "Failed to fetch statistics");
      }
    } catch (err) {
      console.error("Error fetching stats:", err);
      message.error(err.response?.data?.message || "Failed to fetch statistics");
    } finally {
      setLoading(false);
    }
  }, [token, dateBetween, selectedCity, selectedStatus]);

  // Fetch CRM statistics for metrics cards
  const fetchCrmStats = useCallback(async () => {
    try {
      setCrmLoading(true);
      const payload = {
        startDate: dateBetween.startDate.toDate(),
        endDate: dateBetween.endDate.toDate(),
        subType: "", // Over all
        city: selectedCity,
        crmUserId: undefined,
      };

      const response = await getMurgiStatApi(payload, token);
      if (response && response.posts) {
        setCrmStat(response.posts);
      }
    } catch (err) {
      console.error("Error fetching CRM stats:", err);
    } finally {
      setCrmLoading(false);
    }
  }, [token, dateBetween, selectedCity]);

  useEffect(() => {
    fetchStats();
    fetchCrmStats();
  }, [fetchStats, fetchCrmStats]);

  // Handle cell click to fetch posts
  const handleCellClick = useCallback(async (subType, cellType, record) => {
    setPostsModal({
      visible: true,
      posts: [],
      loading: true,
      title: `${formatSubTypeName(subType)} - ${cellType}`,
      pagination: { current: 1, pageSize: 20, total: 0 },
      filters: {},
    });

    const queryParams = {
      pageNo: 1,
      limit: 20,
    };
    if (subType && subType !== "all") {
      queryParams.subType = subType;
    }

    if (dateBetween.startDate) {
      queryParams.startDate = dateBetween.startDate.format("YYYY-MM-DD");
    }
    if (dateBetween.endDate) {
      queryParams.endDate = dateBetween.endDate.format("YYYY-MM-DD");
    }
    if (selectedCity) {
      queryParams.city = selectedCity;
    }

    if (cellType === "buyingCount") {
      queryParams.status = "buying";
    } else if (cellType === "sellingCount") {
      queryParams.status = "selling";
    } else if (cellType === "activeCount") {
      queryParams.isActive = true;
    } else if (cellType === "inactiveCount") {
      queryParams.isActive = false;
    }

    try {
      const response = await getMurghiMandiPostsBySubType(queryParams, token);

      if (response.success) {
        let posts = response.posts?.docs || [];

        if (cellType === "soldCount") {
          posts = posts.filter((post) => post.postSold === true);
        }

        const cellTypeLabel = cellType
          .replace("Count", "")
          .replace(/([A-Z])/g, " $1")
          .trim()
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

        setPostsModal({
          visible: true,
          posts,
          loading: false,
          title: `${formatSubTypeName(subType)} - ${cellTypeLabel}`,
          pagination: {
            current: response.posts?.page || 1,
            pageSize: response.posts?.limit || 20,
            total: response.posts?.totalDocs || posts.length,
          },
          filters: queryParams,
        });
      } else {
        message.error(response.message || "Failed to fetch posts");
        setPostsModal((prev) => ({ ...prev, loading: false }));
      }
    } catch (err) {
      console.error("Error fetching posts:", err);
      message.error(err.response?.data?.message || "Failed to fetch posts");
      setPostsModal((prev) => ({ ...prev, loading: false }));
    }
  }, [token, dateBetween, selectedCity, formatSubTypeName]);

  // Load more posts
  const loadMorePosts = useCallback(async () => {
    setPostsModal((prev) => ({ ...prev, loading: true }));

    const nextPage = postsModal.pagination.current + 1;

    try {
      const response = await getMurghiMandiPostsBySubType(
        {
          ...postsModal.filters,
          pageNo: nextPage,
        },
        token
      );

      if (response.success) {
        let newPosts = response.posts?.docs || [];

        if (postsModal.title.includes("Sold")) {
          newPosts = newPosts.filter((post) => post.postSold === true);
        }

        setPostsModal((prev) => ({
          ...prev,
          posts: [...prev.posts, ...newPosts],
          loading: false,
          pagination: {
            ...prev.pagination,
            current: nextPage,
            total: response.posts?.totalDocs || prev.pagination.total,
          },
        }));
      }
    } catch (err) {
      console.error("Error loading more posts:", err);
      message.error(err.response?.data?.message || "Failed to load more posts");
      setPostsModal((prev) => ({ ...prev, loading: false }));
    }
  }, [postsModal, token]);

  const subTypesData = stats?.subTypes?.filter(
    (item) => item.subType !== "layer" && item.subType !== "layerchick"
  ) || [];

  const totals = {
    totalPosts: stats?.summary?.totalPosts || 0,
    activePosts: stats?.summary?.totalActive || 0,
    inactivePosts: stats?.summary?.totalInactive || 0,
    buyingPosts: 0,
    sellingPosts: 0,
    soldPosts: 0,
    totalViews: 0,
    totalVisits: 0,
    broilerPosts: 0,
    eggPosts: 0,
    chickPosts: 0,
    feedPosts: 0,
  };

  subTypesData.forEach(item => {
    totals.buyingPosts += item.buyingCount || 0;
    totals.sellingPosts += item.sellingCount || 0;
    totals.soldPosts += item.soldCount || 0;
    totals.totalViews += item.totalViews || 0;
    totals.totalVisits += item.totalVisits || 0;

    const subTypeLower = item.subType?.toLowerCase() || "";
    if (subTypeLower.includes("broiler") && !subTypeLower.includes("chick")) {
      totals.broilerPosts += item.totalCount || 0;
    } else if (subTypeLower.includes("egg")) {
      totals.eggPosts += item.totalCount || 0;
    } else if (subTypeLower.includes("chick")) {
      totals.chickPosts += item.totalCount || 0;
    } else {
      totals.feedPosts += item.totalCount || 0;
    }
  });

  // Data for the 12 Premium Metric Cards matching Figma exactly!
  const cardData = [
    {
      title: "Total Seller",
      value: crmStat?.sellerStat?.length || 0,
      colorClass: "blue",
      icon: <TeamOutlined />,
      trend: "1.7%",
      isUp: true,
      onClick: () => handleCellClick("all", "totalCount"),
    },
    {
      title: "Total Buyer",
      value: crmStat?.buyerStat?.length || 0,
      colorClass: "orange",
      icon: <StarOutlined />,
      trend: "2.3%",
      isUp: true,
      onClick: () => handleCellClick("all", "totalCount"),
    },
    {
      title: "View Selling",
      value: !crmStat?.postStat?.length ? 0 : crmStat?.postStat[0]?.views,
      colorClass: "blue",
      icon: <CheckOutlined />,
      trend: "0.9%",
      isUp: true,
      onClick: () => handleCellClick("all", "sellingCount"),
    },
    {
      title: "View Buying",
      value: !crmStat?.buyingPost?.length ? 0 : crmStat?.buyingPost[0]?.views,
      colorClass: "green",
      icon: <CheckOutlined />,
      trend: "3.7%",
      isUp: true,
      onClick: () => handleCellClick("all", "buyingCount"),
    },
    {
      title: "Unit Sold (Selling)",
      value: !crmStat?.postStat?.length ? 0 : crmStat?.postStat[0]?.numberOfPatties,
      colorClass: "red",
      icon: <ShoppingCartOutlined />,
      trend: "-2.0%",
      isUp: false,
    },
    {
      title: "Unit Sold (Buying)",
      value: !crmStat?.buyingPost?.length ? 0 : crmStat?.buyingPost[0]?.numberOfPatties,
      colorClass: "purple",
      icon: <ReloadOutlined />,
      trend: "4.5%",
      isUp: true,
    },
    {
      title: "Total Commission",
      value: crmStat?.postStat?.length ? kFormatter(crmStat?.paymentDetail[0]?.total) : 0,
      colorClass: "blue",
      icon: <TeamOutlined />,
      trend: "1.7%",
      isUp: true,
    },
    {
      title: "Total Posts (Selling)",
      value: !crmStat?.postStat?.length ? 0 : crmStat?.postStat[0]?.count,
      colorClass: "orange",
      icon: <StarOutlined />,
      trend: "2.3%",
      isUp: true,
      onClick: () => handleCellClick("all", "sellingCount"),
    },
    {
      title: "Total Posts (Buying)",
      value: !crmStat?.buyingPost?.length ? 0 : crmStat?.buyingPost[0]?.count,
      colorClass: "blue",
      icon: <CheckOutlined />,
      trend: "0.9%",
      isUp: true,
      onClick: () => handleCellClick("all", "buyingCount"),
    },
    {
      title: "Total Offer",
      value: !crmStat?.dealStat?.length ? 0 : crmStat?.dealStat[0]?.count,
      colorClass: "green",
      icon: <CheckOutlined />,
      trend: "3.7%",
      isUp: true,
    },
    {
      title: "Total Deals",
      value: !crmStat?.dealStat?.length ? 0 : crmStat?.dealStat[0]?.numberOfDeals,
      colorClass: "red",
      icon: <ShoppingCartOutlined />,
      trend: "-2.0%",
      isUp: false,
    },
    {
      title: "Total Products",
      value: !crmStat?.buyingPost?.length ? 0 : crmStat?.buyingPost[0]?.numberOfPatties, // Duplicated exactly like the Figma mockup placeholder logic!
      colorClass: "purple",
      icon: <ReloadOutlined />,
      trend: "4.5%",
      isUp: true,
    },
  ];

  // Table columns
  const columns = [
    {
      title: "SubType",
      dataIndex: "subType",
      key: "subType",
      render: (text) => (
        <span style={{ fontWeight: 700, color: "#1e293b" }}>{formatSubTypeName(text)}</span>
      ),
    },
    {
      title: "Total",
      dataIndex: "totalCount",
      key: "totalCount",
      sorter: (a, b) => a.totalCount - b.totalCount,
      render: (value, record) => (
        <span
          className="clickable-cell"
          onClick={() => handleCellClick(record.subType, "totalCount", record)}
        >
          {value}
        </span>
      ),
    },
    {
      title: "Buying",
      dataIndex: "buyingCount",
      key: "buyingCount",
      sorter: (a, b) => a.buyingCount - b.buyingCount,
      render: (value, record) => (
        <span
          className="clickable-cell"
          onClick={() => handleCellClick(record.subType, "buyingCount", record)}
        >
          {value}
        </span>
      ),
    },
    {
      title: "Selling",
      dataIndex: "sellingCount",
      key: "sellingCount",
      sorter: (a, b) => a.sellingCount - b.sellingCount,
      render: (value, record) => (
        <span
          className="clickable-cell"
          onClick={() => handleCellClick(record.subType, "sellingCount", record)}
        >
          {value}
        </span>
      ),
    },
    {
      title: "Active",
      dataIndex: "activeCount",
      key: "activeCount",
      sorter: (a, b) => a.activeCount - b.activeCount,
      render: (value, record) => (
        <span
          className="clickable-cell"
          onClick={() => handleCellClick(record.subType, "activeCount", record)}
        >
          {value}
        </span>
      ),
    },
    {
      title: "Inactive",
      dataIndex: "inactiveCount",
      key: "inactiveCount",
      sorter: (a, b) => a.inactiveCount - b.inactiveCount,
      render: (value, record) => (
        <span
          className="clickable-cell"
          onClick={() => handleCellClick(record.subType, "inactiveCount", record)}
        >
          {value}
        </span>
      ),
    },
    {
      title: "Sold",
      dataIndex: "soldCount",
      key: "soldCount",
      sorter: (a, b) => a.soldCount - b.soldCount,
      render: (value, record) => (
        <span
          className="clickable-cell"
          onClick={() => handleCellClick(record.subType, "soldCount", record)}
        >
          {value}
        </span>
      ),
    },
    {
      title: "Total Views",
      dataIndex: "totalViews",
      key: "totalViews",
      sorter: (a, b) => a.totalViews - b.totalViews,
      render: (value) => value?.toLocaleString() || 0,
    },
    {
      title: "Total Visits",
      dataIndex: "totalVisits",
      key: "totalVisits",
      sorter: (a, b) => a.totalVisits - b.totalVisits,
      render: (value) => value?.toLocaleString() || 0,
    },
  ];

  // ── "Post Statistics by City & Type" — live customer data ─────────────
  // Server-side search + pagination via auth-service getAllCustomers.
  const [searchPostStatText, setSearchPostStatText] = useState("");
  const [postStatSearch, setPostStatSearch] = useState("");
  const [postStatData, setPostStatData] = useState([]);
  const [postStatLoading, setPostStatLoading] = useState(false);
  const [postStatPagination, setPostStatPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Debounce typing → server search
  useEffect(() => {
    if (searchPostStatText === postStatSearch) return undefined;
    const timer = setTimeout(() => {
      setPostStatPagination((p) => ({ ...p, current: 1 }));
      setPostStatSearch(searchPostStatText);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchPostStatText, postStatSearch]);

  // Engagement status from lastProfileFetched (app open):
  // ≤7d Active · 7–15d Rising Member · >15d/never Idol · blocked Blocked.
  const derivePostStatStatus = (customer) => {
    if (customer.blocked || customer.status === "BLOCKED") return "Blocked";
    const last = customer.lastProfileFetched
      ? moment(customer.lastProfileFetched)
      : null;
    if (!last) return "Idol";
    const daysAgo = moment().diff(last, "days");
    if (daysAgo <= 7) return "Active";
    if (daysAgo <= 15) return "Rising Member";
    return "Idol";
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setPostStatLoading(true);
        const data = await getCustomers(token, {
          page: postStatPagination.current,
          limit: postStatPagination.pageSize,
          search: postStatSearch && postStatSearch.trim() ? postStatSearch.trim() : undefined,
        });
        if (cancelled) return;
        const docs = data.customers?.docs || [];
        setPostStatData(
          docs.map((c) => ({
            key: c._id,
            signupDate: c.createdAt
              ? moment(c.createdAt).format("DD/MM/YYYY, hh:mm A")
              : "N/A",
            name: c.name,
            phoneNumber: c.phoneNumber,
            city: c.city,
            status: derivePostStatStatus(c),
            activeStatus: c.status === "LOGGED_IN" ? "logged_in" : "logged_off",
          }))
        );
        setPostStatPagination((p) => ({
          ...p,
          total: data.customers?.totalDocs || 0,
        }));
      } catch (err) {
        console.error("Failed to fetch post statistics customers", err);
      } finally {
        if (!cancelled) setPostStatLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, postStatSearch, postStatPagination.current, postStatPagination.pageSize]);

  const getStatusTag = (status) => {
    let colorClass = "crm-status-pill-active";
    let text = status || "Active";

    if (status === "Download") {
      colorClass = "crm-status-pill-download";
    } else if (status === "Link Sent") {
      colorClass = "crm-status-pill-link";
    } else if (status === "Active") {
      colorClass = "crm-status-pill-active";
    } else if (status === "Blocked") {
      colorClass = "crm-status-pill-blocked";
    } else if (status === "Golden Member") {
      colorClass = "crm-status-pill-golden";
    } else if (status === "Idol") {
      colorClass = "crm-status-pill-idol";
    } else if (status === "Rising Member") {
      colorClass = "crm-status-pill-rising";
    }

    return <span className={`crm-status-pill ${colorClass}`}>{text}</span>;
  };

  const getActiveStatusCol = (record) => {
    const isOffline = record.activeStatus === "logged_off";
    const text = isOffline ? "logged_off" : "logged_in";
    const color = isOffline ? "#64748b" : "#10b981";

    const menu = (
      <Menu onClick={({ key }) => message.info(`Action triggered: Option ${key}`)}>
        <Menu.Item key="1">View Detail</Menu.Item>
        <Menu.Item key="2">Ok</Menu.Item>
        <Menu.Item key="3">iOS User</Menu.Item>
        <Menu.Item key="4">Not Interested</Menu.Item>
        <Menu.Item key="5">Key Pad User</Menu.Item>
        <Menu.Item key="6">Other Phone No.</Menu.Item>
      </Menu>
    );

    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
        <span style={{ color, fontWeight: 600, fontSize: "13px" }}>{text}</span>
        <Dropdown overlay={menu} trigger={["click"]}>
          <Button 
            type="text" 
            shape="circle" 
            style={{ border: "none", background: "transparent", padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
            icon={<span style={{ fontSize: "18px", fontWeight: "bold", cursor: "pointer", color: "#94a3b8", lineHeight: 1 }}>⋮</span>} 
          />
        </Dropdown>
      </div>
    );
  };

  const postStatColumns = [
    {
      title: "SR.",
      key: "sr",
      render: (text, record, index) => (
        <span style={{ fontWeight: 600, color: "#64748b" }}>
          {(postStatPagination.current - 1) * postStatPagination.pageSize + index + 1}
        </span>
      ),
      width: 60,
      align: "center",
    },
    {
      title: "Date/Time Join",
      dataIndex: "signupDate",
      key: "signupDate",
      render: (date) => (
        <span style={{ color: "#475569", fontWeight: 500 }}>{date}</span>
      ),
      align: "center",
    },
    {
      title: "Full Name",
      dataIndex: "name",
      key: "name",
      render: (name) => (
        <div style={{ display: "flex", alignItems: "center", gap: "10px", textAlign: "left" }}>
          <Avatar 
            style={{ backgroundColor: "#e0e7ff", color: "#576cf6" }} 
            icon={<UserOutlined />} 
          />
          <span style={{ fontWeight: 700, color: "#1e293b" }}>{name || "N/A"}</span>
        </div>
      ),
      align: "left",
    },
    {
      title: "Phone No.",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      render: (phone) => <span style={{ color: "#475569", fontWeight: 600 }}>{phone || "N/A"}</span>,
      align: "center",
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      render: (city) => <span style={{ color: "#475569", fontWeight: 500 }}>{city || "N/A"}</span>,
      align: "center",
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      render: (status) => getStatusTag(status),
      align: "center",
    },
    {
      title: "Active Status",
      key: "activeStatus",
      render: (text, record) => getActiveStatusCol(record),
      align: "center",
    }
  ];


  return (
    <div className="murghi-mandi-stats-by-subtype" style={{ padding: "0" }}>
      {/* Redesigned Premium Header matching Figma precisely */}
      <div className="crm-mandi-header">
        <div className="crm-mandi-title-area">
          <h1 className="crm-mandi-main-title">Murghi Mandi</h1>
          <span className="crm-mandi-subtitle">Poultry Baba CRM System</span>
        </div>

        <div className="crm-mandi-filters">
          {/* Keep filters but style them cleanly */}
          <div style={{ width: "160px" }}>
            <Select
              showSearch
              style={{ width: "100%" }}
              className="crm-mandi-select"
              value={selectedCity}
              placeholder="Select a city"
              optionFilterProp="children"
              onChange={(e) => setSelectedCity(e)}
              allowClear
              filterOption={(input, option) =>
                option.children?.toLowerCase().indexOf(input?.toLowerCase()) >= 0
              }
            >
              <Option value={""}>All Cities</Option>
              {allcities?.map((city) => (
                <Option key={city._id || city.name} value={city.name || city.value}>
                  {city.name || city.value}
                </Option>
              ))}
            </Select>
          </div>

          <div style={{ width: "140px" }}>
            <Select
              style={{ width: "100%" }}
              className="crm-mandi-select"
              value={selectedStatus}
              placeholder="Select status"
              onChange={(e) => setSelectedStatus(e)}
              allowClear
            >
              <Option value={""}>All Status</Option>
              <Option value="buying">Buying</Option>
              <Option value="selling">Selling</Option>
            </Select>
          </div>

          {/* Figma Double Picker Rounded Pills */}
          <div className="crm-mandi-datepicker-wrapper-new">
            <DatePicker
              value={dateBetween.startDate}
              format="YYYY-MM-DD"
              placeholder="From Date"
              className="crm-mandi-datepicker-pill"
              suffixIcon={<CalendarOutlined style={{ color: '#64748b' }} />}
              onChange={(date) => {
                if (date) {
                  setDateBetween((prev) => ({ ...prev, startDate: date }));
                }
              }}
              allowClear={false}
            />
            <DatePicker
              value={dateBetween.endDate}
              format="YYYY-MM-DD"
              placeholder="To Date"
              className="crm-mandi-datepicker-pill"
              suffixIcon={<CalendarOutlined style={{ color: '#64748b' }} />}
              onChange={(date) => {
                if (date) {
                  setDateBetween((prev) => ({ ...prev, endDate: date }));
                }
              }}
              allowClear={false}
            />
          </div>
        </div>
      </div>

      {/* Grid of 12 Premium Metric Cards */}
      <div className="crm-mandi-grid" style={{ padding: "0 10px" }}>
        {cardData.map((card, index) => (
          <div 
            key={index} 
            className={`crm-mandi-card-layout card-color-${card.colorClass} ${card.onClick ? 'crm-mandi-card-layout-clickable' : ''}`}
            onClick={card.onClick}
          >
            {/* Left Container */}
            <div className="crm-mandi-icon-box">
              {card.icon}
            </div>
            
            {/* Right Container */}
            <div className="crm-mandi-content-box">
              <span className="crm-mandi-card-title-new">{card.title}</span>
              {crmLoading || loading ? (
                <div style={{ padding: '2px 0' }}><Spin size="small" /></div>
              ) : (
                <span className="crm-mandi-card-value-new">{card.value?.toLocaleString() || 0}</span>
              )}
              <div className="crm-mandi-trend-new">
                <span className={`crm-mandi-trend-icon-circle ${card.isUp ? 'crm-mandi-trend-circle-up' : 'crm-mandi-trend-circle-down'}`}>
                  {card.isUp ? <RiseOutlined /> : <FallOutlined />}
                </span>
                <span className={card.isUp ? 'crm-mandi-trend-text-up' : 'crm-mandi-trend-text-down'}>
                  {card.isUp ? "+" : ""}{card.trend}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Statistics Table - Completely Edge to Edge & No Rounded Containers */}
      <div style={{ marginTop: "32px", width: "100%" }}>
        <h2 style={{ fontSize: "18px", fontWeight: "700", color: "#1e293b", marginBottom: "16px", paddingLeft: "10px" }}>
          Statistics by SubType
        </h2>
        <Table
          columns={columns}
          dataSource={subTypesData}
          rowKey="subType"
          loading={loading}
          pagination={{
            pageSize: 12,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} subTypes`,
          }}
          className="crm-premium-table"
          scroll={{ x: "max-content" }}
          bordered={false}
        />
      </div>

      {/* Post Statistics by City & Type Section */}
      <div className="crm-premium-table-wrapper-new">
        {/* Header of the new section, flush at the top of the wrapper */}
        <div className="crm-table-header-container-new">
          
          {/* Yellow Badge Title, clipped rounded by parent overflow */}
          <div className="crm-table-tab-title">
            Post Statistics by City & Type
          </div>
          
          {/* Actions: Search & Add Phone Button */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", paddingRight: "20px" }}>
            <Input
              placeholder="Search here"
              value={searchPostStatText}
              onChange={(e) => setSearchPostStatText(e.target.value)}
              suffix={<SearchIcon style={{ color: "#94a3b8", fontSize: "20px" }} />}
              style={{ 
                width: "220px", 
                borderRadius: "9999px", 
                borderColor: "#cbd5e1",
                height: "38px",
                padding: "0 16px"
              }}
            />
            <Button 
              type="primary" 
              style={{ 
                backgroundColor: "#10b981", 
                borderColor: "#10b981", 
                borderRadius: "9999px",
                fontWeight: "600",
                height: "38px",
                display: "flex",
                alignItems: "center"
              }}
              onClick={() => message.success("Add Phone triggered!")}
            >
              + Add Phone #
            </Button>
          </div>
        </div>

        {/* Flush Table inside the wrapper */}
        <Table
          columns={postStatColumns}
          dataSource={postStatData}
          loading={postStatLoading}
          pagination={{
            current: postStatPagination.current,
            pageSize: postStatPagination.pageSize,
            total: postStatPagination.total,
            showSizeChanger: true,
            showTotal: (total, range) => (
              <span style={{ fontSize: "13px", color: "#64748b" }}>
                Showing <b>{range[0]}-{range[1]}</b> from <b>{total}</b> customers
              </span>
            ),
          }}
          onChange={(p) =>
            setPostStatPagination({
              current: p.current,
              pageSize: p.pageSize,
              total: postStatPagination.total,
            })
          }
          className="crm-flush-table"
          scroll={{ x: "max-content" }}
          bordered={false}
        />
      </div>

      {/* Posts Modal */}
      <Modal
        title={postsModal.title}
        visible={postsModal.visible}
        onCancel={() =>
          setPostsModal({
            visible: false,
            posts: [],
            loading: false,
            title: "",
            pagination: { current: 1, pageSize: 20, total: 0 },
            filters: {},
          })
        }
        footer={null}
        width={1200}
        style={{ top: 20 }}
      >
        <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {postsModal.loading && postsModal.posts.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Spin style={{ marginRight: 8 }} /> Loading posts...
            </div>
          ) : postsModal.posts.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              No posts found
            </div>
          ) : (
            <div>
              {postsModal.posts.map((post) => (
                <Card
                  key={post._id}
                  style={{ marginBottom: "16px" }}
                  size="small"
                >
                  <Row gutter={16}>
                    <Col span={24}>
                      <Space direction="vertical" style={{ width: "100%" }}>
                        <div>
                          <Typography.Text strong>Title: </Typography.Text>
                          <Typography.Text>{post.title || "N/A"}</Typography.Text>
                        </div>
                        {post.customerProfile && (
                          <div>
                            <Space>
                              {post.customerProfile.profileImage && (
                                <Avatar
                                  src={post.customerProfile.profileImage}
                                  size="small"
                                />
                              )}
                              <Typography.Text strong>Customer: </Typography.Text>
                              <Typography.Text>
                                {post.customerProfile.name || "N/A"}
                              </Typography.Text>
                              <Typography.Text type="secondary">
                                ({post.customerProfile.phoneNumber || "N/A"})
                              </Typography.Text>
                            </Space>
                          </div>
                        )}
                        <Space wrap>
                          <div>
                            <Typography.Text strong>City: </Typography.Text>
                            <Typography.Text>{post.city || "N/A"}</Typography.Text>
                          </div>
                          <div>
                            <Typography.Text strong>Status: </Typography.Text>
                            <Tag
                              color={
                                post.postDetails?.status === "buying"
                                  ? "blue"
                                  : "green"
                              }
                            >
                              {post.postDetails?.status || "N/A"}
                            </Tag>
                          </div>
                          <div>
                            <Typography.Text strong>Active: </Typography.Text>
                            <Tag color={post.isActive ? "green" : "red"}>
                              {post.isActive ? "Yes" : "No"}
                            </Tag>
                          </div>
                          {post.postSold !== undefined && (
                            <div>
                              <Typography.Text strong>Sold: </Typography.Text>
                              <Tag color={post.postSold ? "green" : "default"}>
                                {post.postSold ? "Yes" : "No"}
                              </Tag>
                            </div>
                          )}
                        </Space>
                        {post.postDetails && (
                          <Space wrap>
                            {post.postDetails.quantity && (
                              <div>
                                <Typography.Text strong>Quantity: </Typography.Text>
                                <Typography.Text>
                                  {post.postDetails.quantity}
                                </Typography.Text>
                              </div>
                            )}
                            {post.postDetails.size && (
                              <div>
                                <Typography.Text strong>Size: </Typography.Text>
                                <Typography.Text>
                                  {post.postDetails.size}
                                </Typography.Text>
                              </div>
                            )}
                            {post.postDetails.demand && (
                              <div>
                                <Typography.Text strong>Demand: </Typography.Text>
                                <Typography.Text>
                                  {post.postDetails.demand}
                                </Typography.Text>
                              </div>
                            )}
                          </Space>
                        )}
                        <Space wrap>
                          <div>
                            <Typography.Text strong>Views: </Typography.Text>
                            <Typography.Text>{post.views || 0}</Typography.Text>
                          </div>
                          <div>
                            <Typography.Text strong>Visits: </Typography.Text>
                            <Typography.Text>{post.visits || 0}</Typography.Text>
                          </div>
                          <div>
                            <Typography.Text strong>Calls: </Typography.Text>
                            <Typography.Text>{post.callCount || 0}</Typography.Text>
                          </div>
                        </Space>
                        <div>
                          <Typography.Text strong>Created: </Typography.Text>
                          <Typography.Text type="secondary">
                            {moment(post.createdAt).format("YYYY-MM-DD HH:mm")}
                          </Typography.Text>
                        </div>
                      </Space>
                    </Col>
                  </Row>
                </Card>
              ))}
            </div>
          )}
          {postsModal.posts.length > 0 &&
            postsModal.pagination.current * postsModal.pagination.pageSize <
              postsModal.pagination.total && (
              <div style={{ textAlign: "center", marginTop: "20px" }}>
                <Button
                  type="primary"
                  onClick={loadMorePosts}
                  loading={postsModal.loading}
                >
                  Load More
                </Button>
              </div>
            )}
          {postsModal.posts.length > 0 && (
            <div style={{ textAlign: "center", marginTop: "10px" }}>
              <Typography.Text type="secondary">
                Showing {postsModal.posts.length} of {postsModal.pagination.total}{" "}
                posts
              </Typography.Text>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default MurghiMandiStatsBySubType;
